import { useState, useEffect, useRef, useMemo } from "react";
import {
  Button,
  Box,
  CardContent,
  Typography,
  Stack,
  Card,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getDevices } from "../../api/deviceAPI";
import { v4 as uuidv4 } from "uuid";
import { postCommand, getCommandResponse } from "../../api/commandAPI";
import { getPlaceIdFromPathname } from "../../utils/pathUtils";
import { getLog } from "../../api/topicDataAPI";

export default function Command() {
  const { t } = useTranslation();
  const location = useLocation();

  // URL'den node_id'yi al (örn: /vending-machines/156 -> 156)
  const node_id = useMemo(() => {
    const rawId = getPlaceIdFromPathname(location.pathname);
    return rawId ? parseInt(rawId, 10) : null;
  }, [location.pathname]);

  const isAdmin = localStorage.getItem("userName") === "admin@letta.com.tr";

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState("");
  const [cmdResponse1, setCmdResponse1] = useState(null);
  const [isLoading1, setIsLoading1] = useState(false);
  const [cmdResponse2, setCmdResponse2] = useState(null);
  const [isLoading2, setIsLoading2] = useState(false);
  const [cmdResponse3, setCmdResponse3] = useState(null);
  const [isLoading3, setIsLoading3] = useState(false);
  const [cmdResponse4, setCmdResponse4] = useState(null);
  const [isLoading4, setIsLoading4] = useState(false);
  const [cmdResponse5, setCmdResponse5] = useState(null);
  const [isLoading5, setIsLoading5] = useState(false);
  const [cmdResponse6, setCmdResponse6] = useState(null);
  const [isLoading6, setIsLoading6] = useState(false);
  const [cmdResponse7, setCmdResponse7] = useState(null);
  const [isLoading7, setIsLoading7] = useState(false);
  const [cmdResponseTest, setCmdResponseTest] = useState(null);
  const [isLoadingTest, setIsLoadingTest] = useState(false);

  // Test Satışı Dialog state'leri
  const [testSaleDialogOpen, setTestSaleDialogOpen] = useState(false);
  const [testSaleStep, setTestSaleStep] = useState("searching"); // "searching", "found", "error"
  const [foundProduct, setFoundProduct] = useState(null);
  const [testSaleError, setTestSaleError] = useState(null);
  const [logSearchInterval, setLogSearchInterval] = useState(null);
  const [remainingTime, setRemainingTime] = useState(60); // Geri sayım için

  const [cmdStr, setCmdStr] = useState("");
  const [cmdVal, setCmdVal] = useState("");
  const [oidReg, setOidReg] = useState("");
  const [oidReg2, setOidReg2] = useState("");
  const [Val, setVal] = useState("");
  const [subject, setSubject] = useState("");
  const [severity, setSeverity] = useState("");

  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    cmdStr: "",
    cmdVal: "",
    oidReg: "",
    oidReg2: "",
    Val: "",
    subject: "",
    severity: "",
  });

  const pollIntervalRef = useRef(null);
  const [isCommandRunning, setIsCommandRunning] = useState(false);

  // Cihazları API'den çek ve node_id'ye göre filtrele
  useEffect(() => {
    async function fetchDevices() {
      if (!node_id) {
        return;
      }

      setDevicesLoading(true);
      try {
        const response = await getDevices();

        // API doğrudan array veya response.data içinde array döndürebilir
        const devicesData = Array.isArray(response) ? response : response.data;

        if (devicesData && Array.isArray(devicesData)) {
          // node_idNode değeri URL'deki node_id ile eşleşen cihazları filtrele
          const filtered = devicesData.filter(
            (device) => device.node_idNode === node_id
          );
          setDevices(filtered);

          // Eğer sadece bir cihaz varsa otomatik seç
          if (filtered.length === 1) {
            setSelectedDevice(filtered[0].idDevice);
          }
        } else {
          setDevices([]);
        }
      } catch (error) {
        console.error("Error fetching devices:", error);
        setDevices([]);
      } finally {
        setDevicesLoading(false);
      }
    }

    fetchDevices();
  }, [node_id]);

  const handleSendCommand = async () => {
    console.log("handleSendCommand:", { node_id, selectedDevice, cmdStr });
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse1(t("nodeComponents.command.selectDeviceError"));
      setIsCommandRunning(false);
      return;
    }

    // Validasyon
    if (!cmdStr.trim()) {
      setFormErrors(prev => ({ ...prev, cmdStr: t("validation.required", "Bu alan zorunludur") }));
      setIsCommandRunning(false);
      return;
    }
    setFormErrors(prev => ({ ...prev, cmdStr: "" }));

    setIsLoading1(true);
    setCmdResponse1(null);
    const cmdKey = uuidv4();

    try {
      await postCommand(node_id, selectedDevice, cmdKey, cmdStr, []);
      console.log("cmdKey:", cmdKey);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );
          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse1(response[0].Value);
            // console.log("response.Value:", response[0].Value);
            setIsLoading1(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse1(t("nodeComponents.command.noResponseError"));
            // console.log("else response", response);

            setIsLoading1(false);
            setIsCommandRunning(false);
          } else {
            console.log("else", response);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse1(t("nodeComponents.command.failedResponseError"));
            setIsLoading1(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading1(false);
      setIsCommandRunning(false);
    }
  };

  const parseProductPacket = (vValue) => {
    // V değeri: 130007D00000EA
    // 1300 => ürün seçildi
    // 07D0 => ürün fiyatı (hex)
    // 0000 => ürün numarası (hex)
    // EA => checksum

    if (!vValue || vValue.length < 12) {
      return null;
    }

    const code = vValue.substring(0, 4); // 1300
    if (code !== "1300") {
      return null;
    }

    const priceHex = vValue.substring(4, 8); // 07D0
    const productNumHex = vValue.substring(8, 12); // 0000

    const price = parseInt(priceHex, 16); // 2000 (decimal)
    const productNum = parseInt(productNumHex, 16); // 0

    return {
      price,
      productNum,
      rawPacket: vValue
    };
  };

  // Dialog'u aç ve log aramasını başlat
  const handleOpenTestSaleDialog = () => {
    if (!selectedDevice) {
      setCmdResponseTest(t("nodeComponents.command.selectDeviceError"));
      return;
    }

    // Dialog'u aç ve başlangıç durumuna getir
    setTestSaleDialogOpen(true);
    setTestSaleStep("searching");
    setFoundProduct(null);
    setTestSaleError(null);
    setRemainingTime(60); // Geri sayımı sıfırla

    // Log aramasını başlat
    startLogSearch();
  };

  // Log aramasını başlat
  const startLogSearch = async () => {
    const startTime = Math.floor(Date.now() / 1000);
    let attempts = 0;
    const maxAttempts = 60;

    const searchInterval = setInterval(async () => {
      attempts++;

      // Geri sayımı güncelle
      setRemainingTime(maxAttempts - attempts);

      try {
        const currentTime = Math.floor(Date.now() / 1000);
        const logsResponse = await getLog(node_id, {
          start: startTime,
          end: currentTime,
          pageSize: 5000
        });

        const logs = Array.isArray(logsResponse) ? logsResponse : [];

        // V:1300 ile başlayan paketi ara
        let foundPacket = null;
        for (const log of logs) {
          if (log.message) {
            const messageStr = String(log.message);

            // {"M":1,"V":"130007D00000EA","P":"00"} formatını ara
            const jsonMatch = messageStr.match(/\{[^}]*"V"\s*:\s*"(1300[0-9A-Fa-f]+)"[^}]*\}/);
            if (jsonMatch && jsonMatch[1]) {
              foundPacket = jsonMatch[1];
              break;
            }

            // Alternatif format: V:1300... şeklinde
            const simpleMatch = messageStr.match(/V\s*:\s*"?(1300[0-9A-Fa-f]+)"?/i);
            if (simpleMatch && simpleMatch[1]) {
              foundPacket = simpleMatch[1];
              break;
            }
          }
        }

        if (foundPacket) {
          clearInterval(searchInterval);
          setLogSearchInterval(null);

          // Paketi parse et
          const parsedData = parseProductPacket(foundPacket);

          if (parsedData) {
            setFoundProduct(parsedData);
            setTestSaleStep("found");
          } else {
            setTestSaleError(`Paket bulundu ama parse edilemedi: ${foundPacket}`);
            setTestSaleStep("error");
          }
        } else if (attempts >= maxAttempts) {
          clearInterval(searchInterval);
          setLogSearchInterval(null);
          setTestSaleError("Ürün seçimi yapılmadı.");
          setTestSaleStep("error");
          // 60 saniye doldu - MDB:P,0 gönder
          if (selectedDevice) {
            const cmdKey = uuidv4();
            postCommand(node_id, selectedDevice, cmdKey, "cliCmd", ["MDB:P,0"]);
          }
        }
      } catch (error) { 
        console.error(`Error checking logs (attempt ${attempts}):`, error);

        if (attempts >= maxAttempts) {
          clearInterval(searchInterval);
          setLogSearchInterval(null);
          setTestSaleError(`Log kontrol edilirken hata oluştu: ${error.message}`);
          setTestSaleStep("error");
        }
      }
    }, 1000);

    setLogSearchInterval(searchInterval);
  };

  // Dialog'u kapat ve interval'i temizle - MDB:P,0 gönder
  const handleCloseTestSaleDialog = async () => {
    if (logSearchInterval) {
      clearInterval(logSearchInterval);
      setLogSearchInterval(null);
    }

    // MDB:P,0 komutu gönder (iptal)
    if (selectedDevice) {
      const cmdKey = uuidv4();
      await postCommand(node_id, selectedDevice, cmdKey, "cliCmd", ["MDB:P,0"]);
    }

    setTestSaleDialogOpen(false);
    setTestSaleStep("searching");
    setFoundProduct(null);
    setTestSaleError(null);
  };

  // Ödeme Yap butonu - mevcut test satışı komutunu gönder
  const handlePayment = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    setIsLoadingTest(true);
    setCmdResponseTest(null);
    const cmdKey = uuidv4();

    try {
      await postCommand(node_id, selectedDevice, cmdKey, "cliCmd", ["MDB:P,1"]);
      console.log("cmdKey:", cmdKey);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );
          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponseTest(response[0].Value);
            setIsLoadingTest(false);
            setIsCommandRunning(false);
            // Dialog'u kapat
            handleCloseTestSaleDialog();
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponseTest(t("nodeComponents.command.noResponseError"));
            setIsLoadingTest(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponseTest(t("nodeComponents.command.failedResponseError"));
            setIsLoadingTest(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoadingTest(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendTest = async () => {
    console.log("handleSendTest:", { node_id, selectedDevice, cmdStr });
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponseTest(t("nodeComponents.command.selectDeviceError"));
      setIsCommandRunning(false);
      return;
    }

    setIsLoadingTest(true);
    setCmdResponseTest(null);
    const cmdKey = uuidv4();

    // Butona tıklandığı andaki timestamp'i al
    const startTime = Math.floor(Date.now() / 1000);

    try {
      await postCommand(node_id, selectedDevice, cmdKey, cmdStr, ["MDB:P,1"]);
      console.log("cmdKey:", cmdKey);
      console.log("Test satışı başlatıldı, loglar kontrol ediliyor...");

      let logCheckAttempts = 0;
      const maxLogCheckAttempts = 60; // 60 saniye

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        logCheckAttempts++;

        try {
          // Şu anki zamana kadar olan logları çek
          const currentTime = Math.floor(Date.now() / 1000);
          const logsResponse = await getLog(node_id, {
            start: startTime,
            end: currentTime,
            pageSize: 5000
          });

          const logs = Array.isArray(logsResponse) ? logsResponse : [];

          // V:1300 ile başlayan paketi ara
          let foundPacket = null;
          for (const log of logs) {
            if (log.message) {
              const messageStr = String(log.message);

              // {"M":1,"V":"130007D00000EA","P":"00"} formatını ara
              const jsonMatch = messageStr.match(/\{[^}]*"V"\s*:\s*"(1300[0-9A-Fa-f]+)"[^}]*\}/);
              if (jsonMatch && jsonMatch[1]) {
                foundPacket = jsonMatch[1];
                break;
              }

              // Alternatif format: V:1300... şeklinde
              const simpleMatch = messageStr.match(/V\s*:\s*"?(1300[0-9A-Fa-f]+)"?/i);
              if (simpleMatch && simpleMatch[1]) {
                foundPacket = simpleMatch[1];
                break;
              }
            }
          }

          if (foundPacket) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;

            // Paketi parse et
            const parsedData = parseProductPacket(foundPacket);

            if (parsedData) {
              const resultMessage = `✓ Test Satışı Başarılı!\n\nÜrün Fiyatı: ${parsedData.price}\nÜrün Numarası: ${parsedData.productNum}\n`;
              setCmdResponseTest(resultMessage);
            } else {
              setCmdResponseTest(`Paket bulundu ama parse edilemedi: ${foundPacket}`);
            }

            setIsLoadingTest(false);
            setIsCommandRunning(false);
            console.log("Ürün paketi bulundu:", foundPacket);
          } else if (logCheckAttempts >= maxLogCheckAttempts) {
            // 60 saniye içinde bulunamadı - timeout
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponseTest("⚠ Timeout: 60 saniye içinde V:1300 ile başlayan paket bulunamadı.");
            setIsLoadingTest(false);
            setIsCommandRunning(false);
            console.log("Timeout: Paket bulunamadı");
          } else {
            console.log(`Log kontrol devam ediyor... (${logCheckAttempts}/${maxLogCheckAttempts})`);
          }
        } catch (error) {
          console.error(
            `Error checking logs (attempt ${logCheckAttempts}):`,
            error
          );

          if (logCheckAttempts >= maxLogCheckAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponseTest(`Hata: Log kontrol edilirken hata oluştu - ${error.message}`);
            setIsLoadingTest(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setCmdResponseTest(`Hata: Komut gönderilemedi - ${error.message}`);
      setIsLoadingTest(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand2 = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse2("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    // Validasyon
    let hasError = false;
    if (!oidReg.trim()) {
      setFormErrors(prev => ({ ...prev, oidReg: t("validation.required", "Bu alan zorunludur") }));
      hasError = true;
    } else {
      setFormErrors(prev => ({ ...prev, oidReg: "" }));
    }
    if (!Val.trim()) {
      setFormErrors(prev => ({ ...prev, Val: t("validation.required", "Bu alan zorunludur") }));
      hasError = true;
    } else {
      setFormErrors(prev => ({ ...prev, Val: "" }));
    }
    if (hasError) {
      setIsCommandRunning(false);
      return;
    }

    setIsLoading2(true);
    setCmdResponse2(null);
    const cmdKey = uuidv4();
    const CmdStr = "oidWrite";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, [
        oidReg.toString(),
        Val.toString(),
      ]);
      // console.log("oidReg:", oidReg);
      // console.log("Val:", Val);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse2(response[0].Value);
            setIsLoading2(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse2("No response received after 30 seconds");
            setIsLoading2(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse2("Failed to get response after multiple attempts");
            setIsLoading2(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading2(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand3 = async () => {
    // console.log("handleSendCommand3:", { node_id, selectedDevice, oidReg2 });
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse3("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    // Validasyon
    if (!oidReg2.trim()) {
      setFormErrors(prev => ({ ...prev, oidReg2: t("validation.required", "Bu alan zorunludur") }));
      setIsCommandRunning(false);
      return;
    }
    setFormErrors(prev => ({ ...prev, oidReg2: "" }));

    setIsLoading3(true);
    setCmdResponse3(null);
    const cmdKey = uuidv4();
    const CmdStr = "oidRead";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, [
        oidReg2.toString(),
      ]);
      // console.log("oidReg:", oidReg2);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse3(response[0].Value);
            setIsLoading3(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse3("No response received after 30 seconds");
            setIsLoading3(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse3("Failed to get response after multiple attempts");
            setIsLoading3(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading3(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand4 = async () => {
    // console.log("handleSendCommand3:", { node_id, selectedDevice, cmdVal });
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse4("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    // Validasyon
    if (!cmdVal.trim()) {
      setFormErrors(prev => ({ ...prev, cmdVal: t("validation.required", "Bu alan zorunludur") }));
      setIsCommandRunning(false);
      return;
    }
    setFormErrors(prev => ({ ...prev, cmdVal: "" }));

    setIsLoading4(true);
    setCmdResponse4(null);
    const cmdKey = uuidv4();
    const CmdStr = "cliCmd";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, [
        cmdVal.toString(),
      ]);
      // console.log("cmdVal:", cmdVal);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse4(response[0].Value);
            setIsLoading4(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse4("No response received after 30 seconds");
            setIsLoading4(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse4("Failed to get response after multiple attempts");
            setIsLoading4(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading4(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand5 = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);

    if (!selectedDevice) {
      setCmdResponse5("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoading5(true);
    setCmdResponse5(null);
    const cmdKey = uuidv4();
    const CmdStr = "reset";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, []);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse5(response[0].Value);
            setIsLoading5(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse5("No response received after 30 seconds");
            setIsLoading5(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse5("Failed to get response after multiple attempts");
            setIsLoading5(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading5(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand6 = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);
    if (!selectedDevice) {
      setCmdResponse6("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    // Validasyon
    let hasError = false;
    if (!subject.trim()) {
      setFormErrors(prev => ({ ...prev, subject: t("validation.required", "Bu alan zorunludur") }));
      hasError = true;
    } else {
      setFormErrors(prev => ({ ...prev, subject: "" }));
    }
    if (!severity.trim()) {
      setFormErrors(prev => ({ ...prev, severity: t("validation.required", "Bu alan zorunludur") }));
      hasError = true;
    } else {
      setFormErrors(prev => ({ ...prev, severity: "" }));
    }
    if (hasError) {
      setIsCommandRunning(false);
      return;
    }

    setIsLoading6(true);
    setCmdResponse6(null);
    const cmdKey = uuidv4();
    const CmdStr = "debug";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, [
        subject.toString(),
        severity.toString(),
      ]);
      // console.log("CmdStr:", CmdStr);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse6(response[0].Value);
            setIsLoading6(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse6("No response received after 30 seconds");
            setIsLoading6(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse6("Failed to get response after multiple attempts");
            setIsLoading6(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading6(false);
      setIsCommandRunning(false);
    }
  };

  const handleSendCommand7 = async () => {
    if (isCommandRunning) return;
    setIsCommandRunning(true);
    if (!selectedDevice) {
      setCmdResponse7("Error: Please select a device first");
      setIsCommandRunning(false);
      return;
    }

    setIsLoading7(true);
    setCmdResponse7(null);
    const cmdKey = uuidv4();
    const CmdStr = "getConfig";

    try {
      await postCommand(node_id, selectedDevice, cmdKey, CmdStr, []);
      let attempts = 0;
      const maxAttempts = 80;

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      const pollInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await getCommandResponse(
            node_id,
            selectedDevice,
            cmdKey
          );

          if (response && response.length > 0) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse7(response[0].Value);
            setIsLoading7(false);
            setIsCommandRunning(false);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse7("No response received after 30 seconds");
            setIsLoading7(false);
            setIsCommandRunning(false);
          }
        } catch (error) {
          console.error(
            `Error getting command response (attempt ${attempts}):`,
            error
          );

          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            pollIntervalRef.current = null;
            setCmdResponse7("Failed to get response after multiple attempts");
            setIsLoading7(false);
            setIsCommandRunning(false);
          }
        }
      }, 1000);

      pollIntervalRef.current = pollInterval;
    } catch (error) {
      console.error("Error sending command:", error);
      setIsLoading7(false);
      setIsCommandRunning(false);
    }
  };

  return (
    <>
      <Box sx={{ maxWidth: { sm: "100%", md: "1700px" }, overflowX: "hidden", px: { xs: 1, sm: 0 } }}>
        <Box sx={{ mt: 2, mb: 2 }}>
          <FormControl fullWidth sx={{ maxWidth: { xs: "100%", sm: 300 } }}>
            <InputLabel id="device-select-label">
              {t("nodeComponents.command.deviceSerial")}
            </InputLabel>
            <Select
              labelId="device-select-label"
              id="device-select"
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              disabled={devicesLoading || devices.length === 0}
              label={t("nodeComponents.command.deviceSerial")}
            >
              {devices.map((device) => (
                <MenuItem key={device.idDevice} value={device.idDevice}>
                  {device.serialDevice}
                </MenuItem>
              ))}
            </Select>
            {devicesLoading && (
              <Typography variant="caption" sx={{ mt: 1 }}>
                {t("nodeComponents.command.loadingDevices")}
              </Typography>
            )}
            {!devicesLoading && devices.length === 0 && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {t("nodeComponents.command.noDevicesFound")}
              </Typography>
            )}
          </FormControl>
        </Box>

        <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ flexGrow: 1 }}
            >
              <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
              <Button
                variant="contained"
                color="success"
                onClick={async () => {
                  if (!selectedDevice) {
                    setCmdResponseTest(t("nodeComponents.command.selectDeviceError"));
                    return;
                  }
                  // MDB:S,8000 komutunu gönder (response beklenmez)
                  const cmdKey = uuidv4();
                  await postCommand(node_id, selectedDevice, cmdKey, "cliCmd", ["MDB:S,8000"]);
                  // Dialog'u aç
                  handleOpenTestSaleDialog();
                }}
                startIcon={<SendIcon />}
                sx={{ height: 45, width: { xs: "100%", sm: 150 }, color: "white" }}
              >
                {t("nodeComponents.command.makeTestSale", "Test Satışı Yap")}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleSendCommand5}
                startIcon={<SendIcon />}
                sx={{ height: 45, width: { xs: "100%", sm: 150 } }}
              >
                {t("nodeComponents.command.resetDevice")}
              </Button>
            </Stack>
            {isLoading5 && (
              <Typography sx={{ mt: 2 }}>
                {t("nodeComponents.command.loadingResponse")}
              </Typography>
            )}{" "}
            {cmdResponse5 && (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  p: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(66, 66, 66, 0.6)"
                      : "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(81, 81, 81, 1)"
                      : "transparent",
                })}
              >
                <Typography variant="subtitle1">
                  {t("nodeComponents.command.commandResponse")}:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {cmdResponse5}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
        {isAdmin && (
          <>
            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ flexGrow: 1 }}
                >
                  <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand7}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: { xs: "100%", sm: 150 } }}
                  >
                    {t("nodeComponents.command.getConfig")}
                  </Button>
                </Stack>
                {isLoading7 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse7 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse7}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.subject")}
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "auto" } }}
                    error={Boolean(formErrors.subject)}
                    helperText={formErrors.subject}
                  />
                  <TextField
                    label={t("nodeComponents.command.severity")}
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "auto" } }}
                    error={Boolean(formErrors.severity)}
                    helperText={formErrors.severity}
                  />
                  <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand6}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: { xs: "100%", sm: 150 } }}
                  >
                    {t("nodeComponents.command.debugMode")}
                  </Button>
                </Stack>
                {isLoading6 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse6 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse6}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.oidReg")}
                    value={oidReg2}
                    onChange={(e) => setOidReg2(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "auto" } }}
                    error={Boolean(formErrors.oidReg2)}
                    helperText={formErrors.oidReg2}
                  />
                  <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand3}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: { xs: "100%", sm: 150 } }}
                  >
                    {t("nodeComponents.command.readRegister")}
                  </Button>
                </Stack>
                {isLoading3 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse3 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse3}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.oidReg")}
                    value={oidReg}
                    onChange={(e) => setOidReg(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "auto" } }}
                    error={Boolean(formErrors.oidReg)}
                    helperText={formErrors.oidReg}
                  />
                  <TextField
                    label={t("nodeComponents.command.value")}
                    value={Val}
                    onChange={(e) => setVal(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "auto" } }}
                    error={Boolean(formErrors.Val)}
                    helperText={formErrors.Val}
                  />
                  <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand2}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: { xs: "100%", sm: 150 } }}
                  >
                    {t("nodeComponents.command.writeRegister")}
                  </Button>
                </Stack>
                {isLoading2 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse2 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse2}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.cmdVal")}
                    value={cmdVal}
                    onChange={(e) => setCmdVal(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: { xs: 0, sm: 2 }, minWidth: { xs: "100%", sm: 600 }, width: { xs: "100%", sm: "auto" } }}
                    error={Boolean(formErrors.cmdVal)}
                    helperText={formErrors.cmdVal}
                  />
                  <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand4}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: { xs: "100%", sm: 150 } }}
                  >
                    {t("nodeComponents.command.sendCliCMD")}
                  </Button>
                </Stack>
                {isLoading4 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse4 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse4}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card
              sx={{
                height: "100%",
                mt: 2,
                mb: 2,
                borderWidth: 2,
                borderStyle: "solid",
              }}
            >
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ flexGrow: 1 }}
                >
                  <TextField
                    label={t("nodeComponents.command.commandString")}
                    value={cmdStr}
                    onChange={(e) => setCmdStr(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mr: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "auto" } }}
                    error={Boolean(formErrors.cmdStr)}
                    helperText={formErrors.cmdStr}
                  />
                  <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />
                  <Button
                    variant="contained"
                    color="third"
                    onClick={handleSendCommand}
                    startIcon={<SendIcon />}
                    sx={{ height: 45, width: { xs: "100%", sm: 150 } }}
                  >
                    {t("nodeComponents.command.sendCommand")}
                  </Button>
                </Stack>
                {isLoading1 && (
                  <Typography sx={{ mt: 2 }}>
                    {t("nodeComponents.command.loadingResponse")}
                  </Typography>
                )}{" "}
                {cmdResponse1 && (
                  <Box
                    sx={(theme) => ({
                      mt: 2,
                      p: 2,
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(66, 66, 66, 0.6)"
                          : "#f5f5f5",
                      borderRadius: 1,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(81, 81, 81, 1)"
                          : "transparent",
                    })}
                  >
                    <Typography variant="subtitle1">
                      {t("nodeComponents.command.commandResponse")}:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {cmdResponse1}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* <Card
          sx={{
            height: "100%",
            mt: 2,
            mb: 2,
            borderWidth: 2,
            borderStyle: "solid",
          }}
        >
          <CardContent>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ flexGrow: 1 }}
            >
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="third"
                onClick={() => {
                  setCmdStr("MDB:P,1");
                  handleSendTest();
                }}
                startIcon={<SendIcon />}
                sx={{ height: 45, width: 200 }}
              >
                Send MDB:P,1
              </Button>
            </Stack>
            {isLoadingTest && (
              <Typography sx={{ mt: 2 }}>
                {t("nodeComponents.command.loadingResponse")}
              </Typography>
            )}{" "}
            {cmdResponseTest && (
              <Box
                sx={(theme) => ({
                  mt: 2,
                  p: 2,
                  bgcolor:
                    theme.palette.mode === "dark"
                      ? "rgba(66, 66, 66, 0.6)"
                      : "#f5f5f5",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(81, 81, 81, 1)"
                      : "transparent",
                })}
              >
                <Typography variant="subtitle1">
                  {t("nodeComponents.command.commandResponse")}:
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {cmdResponseTest}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card> */}
      </Box>

      {/* Test Satışı Dialog */}
      <Dialog
        open={testSaleDialogOpen}
        onClose={handleCloseTestSaleDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {testSaleStep === "error" ? "Hata" : testSaleStep === "found" ? "Ürün Bulundu!" : "Test Satışı"}
        </DialogTitle>
        <DialogContent>
          {testSaleStep === "error" ? (
            <Box sx={{ py: 2 }}>
              <Alert severity="error">
                {testSaleError}
              </Alert>
            </Box>
          ) : (
            <Box sx={{ py: 2 }}>
              {testSaleStep === "searching" && (
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <CircularProgress size={40} />
                  <Typography sx={{ mt: 2 }}>
                    Ürün seçimi bekleniyor...
                  </Typography>
                  <Typography variant="h4" color="primary" sx={{ mt: 2, fontWeight: "bold" }}>
                    {remainingTime}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  </Typography>
                </Box>
              )}

              {testSaleStep === "found" && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  Ürün başarıyla tespit edildi!
                </Alert>
              )}

              <Box sx={{
                bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(66, 66, 66, 0.6)" : "#f5f5f5",
                p: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: (theme) => theme.palette.mode === "dark" ? "rgba(81, 81, 81, 1)" : "#e0e0e0"
              }}>
                <Typography variant="h6" gutterBottom>
                  Ürün Bilgileri
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Ürün Numarası:</strong> {foundProduct ? foundProduct.productNum : "Bekleniyor..."}
                  </Typography>
                  {foundProduct && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                      
                    </Typography>
                  )}
                </Box>
              </Box>

              <Typography sx={{ mt: 2, textAlign: "center", color: "text.secondary" }}>
                {testSaleStep === "searching"
                  ? "Ürün bilgileri yükleniyor, ödeme yapabilirsiniz"
                  : "Ödemeyi tamamlamak için \"Ödeme Yap\" butonuna basın"}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {testSaleStep === "error" ? (
            <>
              <Button onClick={handleCloseTestSaleDialog} color="inherit">
                Kapat
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  setTestSaleStep("searching");
                  setTestSaleError(null);
                  startLogSearch();
                }}
              >
                Tekrar Dene
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseTestSaleDialog} color="inherit">
                İptal
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handlePayment}
                disabled={isLoadingTest}
                startIcon={isLoadingTest ? <CircularProgress size={20} /> : <SendIcon />}
              >
                {isLoadingTest ? "Ödeme Yapılıyor..." : "Ödeme Yap"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
